/**
 * EditMemberModal - 기존 회원의 이름·연락처를 수정하는 모달 컴포넌트
 *
 * - `CreateMemberModal` 패턴을 그대로 따르되, 초기값을 prefill하고
 *   변경된 필드만 백엔드 `updateMember`로 전달한다.
 * - centerId/memberId는 부모(AdminMemberDetailPage)가 URL에서 결정해 전달.
 * - 수정 성공 시 onUpdated 콜백 호출 → 부모가 모달을 닫고 상세를 reload.
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { useUpdateMember } from "@/hooks/useMembers";

interface EditMemberModalProps {
  isOpen: boolean;
  centerId: number;
  memberId: number;
  /** 현재 이름(prefill) */
  initialName: string;
  /** 현재 연락처(prefill, 없으면 빈 문자열) */
  initialMobile: string;
  onClose: () => void;
  onUpdated: () => void;
}

/** 연락처를 숫자만 추출한 정규화된 값으로 변환 */
function normalizeMobile(input: string): string {
  return input.replace(/[^0-9]/g, "");
}

/** 백엔드 contract와 동일한 길이 규칙(10~11자리) */
function isValidMobile(normalized: string): boolean {
  return normalized.length === 10 || normalized.length === 11;
}

/** mutation.error의 message를 안전하게 추출 */
function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }
  return null;
}

export function EditMemberModal({
  isOpen,
  centerId,
  memberId,
  initialName,
  initialMobile,
  onClose,
  onUpdated,
}: EditMemberModalProps) {
  const [name, setName] = useState(initialName);
  const [mobile, setMobile] = useState(initialMobile);
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateMember = useUpdateMember();

  // 모달이 열릴 때 초기값으로 리셋
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setMobile(initialMobile);
      setValidationError(null);
      updateMember.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialName, initialMobile]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const normalizedMobile = normalizeMobile(mobile);

    if (!trimmedName) {
      setValidationError("이름을 입력해주세요.");
      return;
    }
    if (!normalizedMobile) {
      setValidationError("연락처를 입력해주세요.");
      return;
    }
    if (!isValidMobile(normalizedMobile)) {
      setValidationError("연락처는 숫자 10~11자리로 입력해주세요.");
      return;
    }

    // 변경된 필드만 전달 (둘 다 그대로면 백엔드 invalid-argument 방지)
    const normalizedInitialMobile = normalizeMobile(initialMobile);
    const params: { centerId: number; memberId: number; name?: string; mobile?: string } = {
      centerId,
      memberId,
    };
    if (trimmedName !== initialName.trim()) params.name = trimmedName;
    if (normalizedMobile !== normalizedInitialMobile) params.mobile = normalizedMobile;

    if (params.name === undefined && params.mobile === undefined) {
      setValidationError("변경된 내용이 없습니다.");
      return;
    }

    setValidationError(null);

    updateMember.mutate(params, {
      onSuccess: () => {
        onUpdated();
      },
    });
  };

  const isSubmitDisabled = !name.trim() || !mobile.trim() || updateMember.isPending;
  const serverErrorMessage = updateMember.isError ? getErrorMessage(updateMember.error) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">회원 정보 수정</h2>

        <form onSubmit={handleSubmit}>
          {/* 이름 (필수) */}
          <div className="mb-4">
            <label
              htmlFor="editMemberName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              id="editMemberName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 연락처 (필수) */}
          <div className="mb-4">
            <label
              htmlFor="editMemberMobile"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              연락처 <span className="text-red-500">*</span>
            </label>
            <input
              id="editMemberMobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="예: 010-1234-5678"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {validationError && <p className="text-sm text-red-600 mb-4">{validationError}</p>}

          {updateMember.isError && !validationError && (
            <p className="text-sm text-red-600 mb-4">
              {serverErrorMessage ?? "회원 수정에 실패했습니다. 다시 시도해주세요."}
            </p>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMember.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
